import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BatchClient, CommandName, JobStatus, BatchJob } from '../../../infrastructure/BatchClient';
import { getConfig } from '../../../config';

interface MatchStatus {
  lastAIMatchedJob: BatchJob | null;
}

export const useCompetitorJobStatus = () => {
  const config = getConfig();
  const batchClient = new BatchClient(config.apiUrl);

  const getMatchStatus = useCallback(async (): Promise<MatchStatus> => {
    try {
      const batchResponse = await batchClient.listBatchJobs({
        commandName: CommandName.MATCH_SKU,
      });
      const jobsCopy = [...batchResponse.jobs];
      const sortedJobs = jobsCopy.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      const firstJob = sortedJobs && sortedJobs.length > 0 ? sortedJobs[0] : null;
      return {
        lastAIMatchedJob: firstJob as BatchJob | null,
      };
    } catch (error) {
      console.error('Failed to fetch batch jobs:', error);
      return {
        lastAIMatchedJob: null,
      };
    }
  }, [batchClient]);

  const getJobsByStatus = useCallback(
    async (status: JobStatus, commandName: CommandName): Promise<BatchJob[]> => {
      try {
        const batchResponse = await batchClient.listBatchJobs({
          status,
          commandName,
        });
        return batchResponse.jobs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      } catch (error) {
        console.error(`Failed to fetch ${status} jobs:`, error);
        return [];
      }
    },
    [batchClient],
  );

  const getActiveJobs = useCallback(
    async (commandName: CommandName): Promise<BatchJob[]> => {
      try {
        const activeStatuses: JobStatus[] = ['SUBMITTED', 'PENDING', 'RUNNABLE', 'STARTING', 'RUNNING'];
        const batchResponse = await batchClient.listBatchJobs({
          commandName,
        });

        const activeJobs = batchResponse.jobs.filter((job) => activeStatuses.includes(job.status as JobStatus));

        return activeJobs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      } catch (error) {
        console.error('Failed to fetch active jobs:', error);
        return [];
      }
    },
    [batchClient],
  );

  const getSucceededJobs = useCallback(
    async (commandName: CommandName): Promise<BatchJob[]> => {
      return getJobsByStatus('SUCCEEDED', commandName);
    },
    [getJobsByStatus],
  );

  const getRunningJobs = useCallback(
    async (commandName: CommandName): Promise<BatchJob[]> => {
      return getActiveJobs(commandName);
    },
    [getActiveJobs],
  );

  const matchStatusQuery = useQuery({
    queryKey: ['matchStatus'],
    queryFn: getMatchStatus,
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const succeededSKUMatchJobsQuery = useQuery({
    queryKey: ['succeededJobs'],
    queryFn: () => getSucceededJobs(CommandName.MATCH_SKU),
    refetchInterval: 30000,
    staleTime: 10000,
  });

  const runningSKUMatchJobsQuery = useQuery({
    queryKey: ['runningJobs'],
    queryFn: () => getActiveJobs(CommandName.MATCH_SKU),
    refetchInterval: 10000,
    staleTime: 5000,
  });

  return {
    getMatchStatus,
    getJobsByStatus,
    getSucceededJobs,
    getRunningJobs,
    getActiveJobs,
    matchStatusQuery,
    succeededSKUMatchJobsQuery,
    runningSKUMatchJobsQuery,
  };
};

